import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { firestoreService, FileMetadata } from '../services/firestoreService';
import { storage } from '../config/firebase';
import { ref, getBytes } from 'firebase/storage';
import FileTreeNode from '../components/FileTreeNode';
import TokenCountdownTimer from '../components/TokenCountdownTimer';
import { AlertCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const DownloadPage: React.FC = () => {
  const { tokenUser, isTokenUser } = useAuth();
  const navigate = useNavigate();
  const [filesByYear, setFilesByYear] = useState<Map<number, FileMetadata[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isTokenUser) {
      navigate('/login');
      return;
    }

    const fetchFiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const files = await firestoreService.getFilesByYear();
        setFilesByYear(files);

        if (tokenUser) {
          await firestoreService.logActivity(tokenUser.sessionId, 'page_view', {
            details: { page: '/download' },
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to load files: ${errorMessage}`);
        toast.error('Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [isTokenUser, navigate, tokenUser]);

  const handleDownload = async (file: FileMetadata) => {
    if (!tokenUser) {
      toast.error('Session expired');
      navigate('/login');
      return;
    }

    try {
      toast.loading('Preparing download...');

      const fileRef = ref(storage, file.storagePath);
      const { getDownloadURL } = await import('firebase/storage');
      const downloadUrl = await getDownloadURL(fileRef);

      await firestoreService.logDownload(
        tokenUser.sessionId,
        file.id,
        file.fileName,
        file.fileSize
      );

      await firestoreService.logActivity(tokenUser.sessionId, 'download', {
        fileName: file.fileName,
        details: { fileSize: file.fileSize },
      });

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success(`Downloading: ${file.fileName}`);
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to download file');
    }
  };

  if (!isTokenUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TokenCountdownTimer />

      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            File Downloads
          </h1>
          <p className="text-gray-600">
            Select a year to view and download available files
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-indigo-600" size={32} />
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-900">Large File Downloads</p>
                <p className="text-sm text-amber-800 mt-1">
                  Files are 500-700 MB in size and may take several minutes to download depending on your internet connection.
                  Please keep this window open until the download completes.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && filesByYear.size === 0 && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-6 text-center">
            <p className="text-blue-900">No files available yet</p>
          </div>
        )}

        {!loading && !error && filesByYear.size > 0 && (
          <div className="space-y-4">
            {Array.from(filesByYear.entries())
              .sort(([yearA], [yearB]) => yearB - yearA)
              .map(([year, files]) => (
                <div
                  key={year}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                >
                  <FileTreeNode
                    year={year}
                    files={files}
                    onDownload={handleDownload}
                    isLoading={loading}
                  />
                </div>
              ))}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Your access token will expire after 24 hours from your first login.
            Make sure to download all files you need before the token expires.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
